import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { publicApi } from "@/lib/api";
import { inr } from "@/lib/brand";
import { SearchBar } from "@/components/site/SearchBar";
import { CarCard, savingsText } from "@/components/site/CarCard";
import { Slider } from "@/components/ui/slider";

const FILTERS = [
  { key: "type", label: "Vehicle category" },
  { key: "transmission", label: "Transmission" },
  { key: "fuel", label: "Fuel type" },
];
const SORTS = [["recommended", "Recommended"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["rating", "Highest rated"], ["popular", "Most popular"], ["newest", "Newest models"]];
const KEEP = ["pickup", "return", "driver", "airport"];
const PRICE_MAX = 300;

function Chip({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-200 ${active ? "border-hc-green bg-hc-green text-white" : "border-hc-sage bg-white text-hc-ink hover:border-hc-green hover:text-hc-green"}`}>
      {children}
    </button>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-hc-slate">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function PriceFilter({ params, setParams }) {
  const [range, setRange] = useState([Number(params.get("price_min") || 0), Number(params.get("price_max") || PRICE_MAX)]);
  const commit = (v) => {
    const p = new URLSearchParams(params);
    v[0] > 0 ? p.set("price_min", v[0]) : p.delete("price_min");
    v[1] < PRICE_MAX ? p.set("price_max", v[1]) : p.delete("price_max");
    setParams(p, { replace: true });
  };
  return (
    <div data-testid="filter-price">
      <p className="mb-3 flex justify-between text-[11px] font-semibold uppercase tracking-[0.15em] text-hc-slate">Price per hour
        <span className="tabular normal-case tracking-normal text-hc-ink" data-testid="filter-price-label">{inr(range[0])} – {range[1] >= PRICE_MAX ? `${inr(PRICE_MAX)}+` : inr(range[1])}</span></p>
      <Slider min={0} max={PRICE_MAX} step={10} value={range} onValueChange={setRange} onValueCommit={commit} data-testid="filter-price-slider" />
    </div>
  );
}

function Filters({ meta, params, setParams, setParam }) {
  return (
    <div className="mt-6 space-y-7">
      {FILTERS.map((f) => (
        <Group key={f.key} label={f.label}>
          {meta[f.key].map((v) => <Chip key={v} active={params.get(f.key) === v} onClick={() => setParam(f.key, v)} testId={`filter-${f.key}-${v}`}>{v}</Chip>)}
        </Group>
      ))}
      <Group label="Seating capacity">
        {[4, 5, 7].map((s) => <Chip key={s} active={params.get("seats") === String(s)} onClick={() => setParam("seats", s)} testId={`filter-seats-${s}`}>{s}+ seats</Chip>)}
      </Group>
      <PriceFilter key={`${params.get("price_min")}-${params.get("price_max")}`} params={params} setParams={setParams} />
      <Group label="Rating">
        <Chip active={params.get("min_rating") === "4"} onClick={() => setParam("min_rating", "4")} testId="filter-rating-4">4.0 ★ & above</Chip>
      </Group>
      <Group label="Rental type">
        <Chip active={params.get("driver") !== "1"} onClick={() => setParam("driver", "")} testId="filter-mode-self">Self-drive</Chip>
        <Chip active={params.get("driver") === "1"} onClick={() => setParam("driver", "1")} testId="filter-mode-driver">With driver</Chip>
      </Group>
      <button onClick={() => setParams(Object.fromEntries(KEEP.filter((k) => params.get(k)).map((k) => [k, params.get(k)])))} data-testid="filters-clear" className="text-sm font-semibold text-hc-green hover:underline">Clear all filters</button>
    </div>
  );
}

function Skeletons() {
  return Array.from({ length: 6 }).map((_, i) => (
    <div key={i} className="overflow-hidden rounded-[18px] border border-hc-sage bg-white" data-testid="car-skeleton">
      <div className="skeleton aspect-[3/2] rounded-none" />
      <div className="space-y-2 p-5"><div className="skeleton h-4 w-1/2" /><div className="skeleton h-5 w-3/4" /><div className="skeleton h-4 w-2/3" /></div>
    </div>
  ));
}

export default function Cars() {
  const [params, setParams] = useSearchParams();
  const [cars, setCars] = useState(null);
  const [error, setError] = useState(false);
  const [meta, setMeta] = useState({ city: [], type: [], transmission: [], fuel: [] });
  const [savings, setSavings] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    publicApi.get("/cars/meta").then((r) => setMeta(r.data)).catch(() => {});
    publicApi.get("/pricing").then((r) => setSavings(savingsText(r.data.duration_discounts))).catch(() => {});
  }, []);

  const query = useMemo(() => {
    const q = { sort: "recommended" };
    ["type", "transmission", "fuel", "city", "seats", "sort", "price_min", "price_max", "min_rating"].forEach((k) => params.get(k) && (q[k] = params.get(k)));
    return q;
  }, [params]);

  const [reload, setReload] = useState(0);
  useEffect(() => {
    setCars(null);
    setError(false);
    publicApi.get("/cars", { params: query }).then((r) => setCars(r.data)).catch(() => setError(true));
  }, [query, reload]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || p.get(k) === String(v)) p.delete(k); else p.set(k, v);
    setParams(p, { replace: true });
  };

  const keep = KEEP.filter((k) => params.get(k)).map((k) => `${k}=${encodeURIComponent(params.get(k))}`).join("&");
  const dateSearch = keep ? `?${keep}` : "";

  return (
    <div className="container-x py-8 sm:py-10" data-testid="cars-page">
      <SearchBar compact cities={meta.city} initial={{ city: params.get("city") || "", driver: params.get("driver") === "1", airport: params.get("airport") === "1", pickup: params.get("pickup") || undefined, ret: params.get("return") || undefined }} />
      <div className="mt-8 grid gap-8 lg:grid-cols-[270px_1fr]">
        <aside className={`card-hc h-fit p-5 lg:sticky lg:top-24 ${showFilters ? "" : "hidden lg:block"}`} data-testid="cars-filters">
          <p className="flex items-center justify-between font-display text-lg font-bold"><span className="flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-hc-green" />Filters</span>
            <button className="lg:hidden" onClick={() => setShowFilters(false)} aria-label="Close filters" data-testid="cars-filters-close"><X className="h-5 w-5" /></button></p>
          <Filters meta={meta} params={params} setParams={setParams} setParam={setParam} />
        </aside>
        <section>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-display text-2xl font-bold tracking-tight text-hc-ink" data-testid="cars-count">
              {cars ? `${cars.length} car${cars.length === 1 ? "" : "s"} available` : error ? "Could not load cars" : "Finding cars…"}
            </h1>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFilters(true)} className="btn-outline !py-2 lg:hidden" data-testid="cars-filters-toggle"><SlidersHorizontal className="h-4 w-4" />Filters</button>
              <select data-testid="cars-sort" value={params.get("sort") || "recommended"} onChange={(e) => setParam("sort", e.target.value)} className="rounded-xl border border-hc-sage bg-white px-4 py-2 text-sm text-hc-ink outline-none focus:ring-2 focus:ring-hc-green">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          {error && (
            <div className="rounded-2xl border border-hc-error/30 bg-white p-10 text-center" data-testid="cars-error">
              <p className="text-hc-ink">We couldn't load cars right now.</p>
              <button onClick={() => setReload((n) => n + 1)} className="btn-primary mt-4" data-testid="cars-retry">Try again</button>
            </div>
          )}
          {cars && cars.length === 0 && (
            <div className="rounded-2xl border border-dashed border-hc-sage bg-white p-12 text-center text-hc-slate" data-testid="cars-empty">No cars match these filters. Try clearing a few.</div>
          )}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {cars ? cars.map((c, i) => <CarCard key={c.id} car={c} index={i} search={dateSearch} savings={savings} />) : !error && <Skeletons />}
          </div>
        </section>
      </div>
    </div>
  );
}
